import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Loader2, Check, ShieldCheck } from "lucide-react";
import { trackEvent } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookingForm = React.forwardRef((props, ref) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", weight_goal: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/consultations`, payload);
      setSuccess(true);
      trackEvent("online_form_submitted", { has_email: !!form.email });
      toast.success("Thank you. Dr. Shalini's team will contact you shortly.");
      setForm({ name: "", phone: "", email: "", weight_goal: "", message: "" });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit. Please try WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" ref={ref} className="py-24 md:py-32 bg-[#FAFAF7]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EAE7E1] px-3.5 py-1.5 text-xs font-semibold text-[#779D7C]">
            Can't visit the clinic?
          </span>
          <h2 className="mt-5 font-display text-4xl md:text-5xl leading-[1.05] text-[#242220]">
            Book an <em className="text-[#EB8771]">online video consultation</em>.
          </h2>
          <p className="mt-5 text-[#38312C] leading-relaxed max-w-md">
            Live outside Hyderabad or can't come in person? Share a few details and
            Dr. Shalini's team will schedule a secure online consultation within one
            working day. Medication is delivered cold‑chain to your door across
            Telangana & Andhra Pradesh.
          </p>

          <div className="mt-8 space-y-4">
            <div className="rounded-2xl bg-[#EEF5EF] border border-[#779D7C]/20 p-5">
              <p className="font-micro text-[#2E936F]">Better to visit in person</p>
              <p className="mt-2 text-[15px] text-[#242220] font-medium leading-snug">
                The first visit is best done at the clinic — so Dr. Shalini can run a full
                body composition analysis (BIA scan) and personally teach you how to self‑administer
                the injection safely.
              </p>
              <div className="mt-4 pt-4 border-t border-[#779D7C]/20">
                <p className="text-sm text-[#242220] font-medium">
                  Madinaguda Clinic · Mon – Sat · 11 AM – 3:30 PM
                </p>
                <p className="text-sm text-[#38312C]">
                  Beside Mangalya Shopping Mall, Madinaguda, Hyderabad
                </p>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  data-testid="form-call-clinic"
                  href="tel:+919133552279"
                  onClick={() => trackEvent("call_clicked", { location: "booking_form" })}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#EAE7E1] text-[#242220] px-4 py-2 text-xs font-medium hover:bg-[#FAFAF7]"
                >
                  Call 91335 52279
                </a>
                <a
                  data-testid="form-whatsapp-link"
                  href="https://wa.me/919133552279?text=Hi%2C%20I%27d%20like%20to%20visit%20the%20clinic%20in%20Madinaguda."
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackEvent("whatsapp_clicked", { location: "booking_form_visit" })}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] text-white px-4 py-2 text-xs font-medium hover:bg-[#1ebe5b]"
                >
                  WhatsApp to visit
                </a>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#6B6660]">
              <ShieldCheck className="h-4 w-4 text-[#779D7C]" strokeWidth={1.7} />
              100% confidential · shared only with Dr. Shalini's team.
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="booking-form"
            noValidate
            className="rounded-[32px] bg-white border border-[#EAE7E1] p-8 md:p-10 shadow-[0_18px_40px_-18px_rgba(10,41,32,0.14)]"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name" className="text-xs tracking-[0.14em] uppercase text-[#6B6660]">Full name *</Label>
                <Input
                  id="name" data-testid="form-name" value={form.name} onChange={update("name")}
                  placeholder="Anitha Reddy"
                  className="mt-2 h-12 rounded-xl border-[#EAE7E1] focus-visible:ring-[#779D7C]"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-xs tracking-[0.14em] uppercase text-[#6B6660]">Phone *</Label>
                <Input
                  id="phone" data-testid="form-phone" value={form.phone} onChange={update("phone")}
                  placeholder="+91 98765 43210"
                  className="mt-2 h-12 rounded-xl border-[#EAE7E1] focus-visible:ring-[#779D7C]"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-xs tracking-[0.14em] uppercase text-[#6B6660]">Email</Label>
                <Input
                  id="email" type="email" data-testid="form-email" value={form.email} onChange={update("email")}
                  placeholder="you@example.com"
                  className="mt-2 h-12 rounded-xl border-[#EAE7E1] focus-visible:ring-[#779D7C]"
                />
              </div>
              <div>
                <Label htmlFor="goal" className="text-xs tracking-[0.14em] uppercase text-[#6B6660]">Weight goal</Label>
                <Input
                  id="goal" data-testid="form-goal" value={form.weight_goal} onChange={update("weight_goal")}
                  placeholder="e.g. lose 15 kg"
                  className="mt-2 h-12 rounded-xl border-[#EAE7E1] focus-visible:ring-[#779D7C]"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="message" className="text-xs tracking-[0.14em] uppercase text-[#6B6660]">
                  Anything we should know
                </Label>
                <Textarea
                  id="message" data-testid="form-message" value={form.message} onChange={update("message")}
                  rows={4}
                  placeholder="Diabetes, thyroid, PCOS, previous weight loss attempts…"
                  className="mt-2 rounded-xl border-[#EAE7E1] focus-visible:ring-[#779D7C]"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="form-submit"
              disabled={loading}
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#EB8771] hover:bg-[#E2725C] text-white px-7 py-4 text-[15px] font-medium shadow-[0_14px_30px_-14px_rgba(255,122,89,0.65)] transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed w-full md:w-auto"
            >
              {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>)
                : success ? (<><Check className="h-4 w-4" /> Request received</>)
                : "Request Online Consultation"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
});
BookingForm.displayName = "BookingForm";
